import type { Role, SessionProfile } from "../../shared/types/domain";
import { demoOrganizationRepository } from "../organization/demoOrganizationRepository";
import type { GovernanceState } from "./governance.types";
const KEY = "boom-v4-governance",
  storeId = "20000000-0000-4000-8000-000000000001";
const seed: GovernanceState = {
  targets: [
    {
      id: "70000000-0000-4000-8000-000000000001",
      name: "武汉天地店月度实收",
      metric: "revenue",
      period: "2026-07",
      targetValue: 240000,
      actualValue: 186000,
      scope: "store",
      storeId,
      createdBy: "10000000-0000-4000-8000-000000000003",
      createdAt: "2026-07-01T00:00:00.000Z",
    },
    {
      id: "70000000-0000-4000-8000-000000000002",
      name: "公司月度课消",
      metric: "lessons",
      period: "2026-07",
      targetValue: 5000,
      actualValue: 3842,
      scope: "company",
      storeId: null,
      createdBy: "10000000-0000-4000-8000-000000000004",
      createdAt: "2026-07-01T00:00:00.000Z",
    },
  ],
  announcements: [
    {
      id: "71000000-0000-4000-8000-000000000001",
      title: "暑期课程服务标准更新",
      content: "课后反馈请在课消后24小时内完成；低评分事项由店长当日回访。",
      audience: ["employee", "manager"],
      scope: "company",
      storeId: null,
      publishedBy: "10000000-0000-4000-8000-000000000004",
      publishedAt: "2026-07-18T08:00:00.000Z",
      active: true,
    },
    {
      id: "71000000-0000-4000-8000-000000000002",
      title: "武汉天地店周末停车提示",
      content: "周末商圈客流较大，建议会员提前15分钟到店。",
      audience: ["member", "employee"],
      scope: "store",
      storeId,
      publishedBy: "10000000-0000-4000-8000-000000000003",
      publishedAt: "2026-07-19T08:00:00.000Z",
      active: true,
    },
  ],
  notifications: [],
  audit: [],
};
const clone = <T>(x: T): T => structuredClone(x);
function load() {
  const raw = localStorage.getItem(KEY);
  if (!raw) return clone(seed);
  try {
    const parsed = JSON.parse(raw) as GovernanceState;
    return { ...parsed, notifications: parsed.notifications ?? [] };
  } catch {
    return clone(seed);
  }
}
function save(s: GovernanceState) {
  localStorage.setItem(KEY, JSON.stringify(s));
}
function fail(m: string): never {
  throw new Error(m);
}
async function context(a: SessionProfile) {
  const org = await demoOrganizationRepository.getState();
  if (!org.accounts.some((x) => x.id === a.id && x.status === "active"))
    fail("当前账号不可用。");
  return org;
}
function audit(
  s: GovernanceState,
  a: SessionProfile,
  action: string,
  type: string,
  id: string,
  reason: string,
) {
  s.audit.unshift({
    id: crypto.randomUUID(),
    actorId: a.id,
    action,
    entityType: type,
    entityId: id,
    reason,
    occurredAt: new Date().toISOString(),
  });
}
export const demoGovernanceRepository = {
  async getState() {
    return load();
  },
  async reset() {
    localStorage.removeItem(KEY);
    return clone(seed);
  },
  async createTarget(
    actor: SessionProfile,
    input: {
      name: string;
      metric: "revenue" | "lessons" | "members" | "rating";
      period: string;
      targetValue: number;
      scope: "company" | "store";
      storeId: string | null;
    },
  ) {
    await context(actor);
    if (!["manager", "super"].includes(actor.role))
      fail("只有店长或超管可以创建目标。");
    if (
      actor.role === "manager" &&
      (input.scope !== "store" || input.storeId !== actor.storeId)
    )
      fail("店长只能创建本店目标。");
    if (
      (input.scope === "store" && !input.storeId) ||
      (input.scope === "company" && input.storeId)
    )
      fail("请选择全公司目标或一个具体门店。");
    if (
      !input.name.trim() ||
      !/^\d{4}-\d{2}$/.test(input.period) ||
      input.targetValue <= 0
    )
      fail("目标名称、周期和目标值无效。");
    const s = load(),
      next = {
        id: crypto.randomUUID(),
        name: input.name.trim(),
        metric: input.metric,
        period: input.period,
        targetValue: input.targetValue,
        actualValue: 0,
        scope: input.scope,
        storeId: input.scope === "company" ? null : input.storeId,
        createdBy: actor.id,
        createdAt: new Date().toISOString(),
      };
    s.targets.unshift(next);
    audit(s, actor, "target.create", "business_target", next.id, next.name);
    save(s);
    return clone(next);
  },
  async updateActual(
    actor: SessionProfile,
    targetId: string,
    actualValue: number,
  ) {
    await context(actor);
    if (!["manager", "super"].includes(actor.role))
      fail("当前角色不能更新目标进度。");
    const s = load(),
      target = s.targets.find((x) => x.id === targetId) ?? fail("目标不存在。");
    if (actor.role === "manager" && target.storeId !== actor.storeId)
      fail("不能更新其他门店目标。");
    if (actualValue < 0) fail("实际值不能为负。");
    target.actualValue = actualValue;
    audit(
      s,
      actor,
      "target.progress",
      "business_target",
      target.id,
      `实际值${actualValue}`,
    );
    save(s);
    return clone(target);
  },
  async publishAnnouncement(
    actor: SessionProfile,
    input: {
      title: string;
      content: string;
      audience: Role[];
      scope: "company" | "store";
      storeId: string | null;
    },
  ) {
    await context(actor);
    if (!["manager", "super"].includes(actor.role))
      fail("只有店长或超管可以发布公告。");
    if (
      actor.role === "manager" &&
      (input.scope !== "store" || input.storeId !== actor.storeId)
    )
      fail("店长只能发布本店公告。");
    if (
      !input.title.trim() ||
      input.content.trim().length < 4 ||
      !input.audience.length
    )
      fail("公告标题、内容和受众不能为空。");
    const s = load(),
      next = {
        id: crypto.randomUUID(),
        title: input.title.trim(),
        content: input.content.trim(),
        audience: [...new Set(input.audience)],
        scope: input.scope,
        storeId: input.scope === "company" ? null : input.storeId,
        publishedBy: actor.id,
        publishedAt: new Date().toISOString(),
        active: true,
      };
    s.announcements.unshift(next);
    audit(
      s,
      actor,
      "announcement.publish",
      "announcement",
      next.id,
      next.title,
    );
    save(s);
    return clone(next);
  },
  visibleAnnouncements(role: Role, actorStoreId: string | null) {
    return load().announcements.filter(
      (x) =>
        x.active &&
        x.audience.includes(role) &&
        (x.scope === "company" || x.storeId === actorStoreId),
    );
  },
  recordExport(actor: SessionProfile, entity: string) {
    const s = load();
    audit(s, actor, "data.export", "export", crypto.randomUUID(), entity);
    save(s);
  },
  async markNotificationRead(actor: SessionProfile, notificationId: string) {
    await context(actor);
    const s = load();
    const item =
      s.notifications.find(
        (notification) => notification.id === notificationId,
      ) ?? fail("通知不存在。");
    if (item.recipientId !== actor.id) fail("只能处理自己的通知。");
    item.readAt ??= new Date().toISOString();
    save(s);
    return clone(item);
  },
  async safeDelete(
    actor: SessionProfile,
    input: { entityType: string; targetId: string; reason: string },
  ) {
    if (actor.role !== "super") fail("仅超管可以执行统一删除。");
    if (input.reason.trim().length < 2) fail("请填写删除原因。");
    const s = load();
    audit(
      s,
      actor,
      "super.safe_delete",
      input.entityType,
      input.targetId,
      input.reason,
    );
    save(s);
    return { id: input.targetId, mode: "archived", entityType: input.entityType };
  },
};

export const demoNotificationAdapter = {
  notify(
    input: Omit<
      GovernanceState["notifications"][number],
      "id" | "createdAt" | "readAt"
    > & { dedupeKey: string },
  ) {
    const s = load();
    if (
      s.notifications.some(
        (item) =>
          `${item.entityType}:${item.entityId}:${item.type}` ===
          input.dedupeKey,
      )
    )
      return;
    s.notifications.unshift({
      id: crypto.randomUUID(),
      recipientId: input.recipientId,
      storeId: input.storeId,
      type: input.type,
      title: input.title,
      content: input.content,
      entityType: input.entityType,
      entityId: input.entityId,
      createdAt: new Date().toISOString(),
      readAt: null,
    });
    save(s);
  },
};

export const demoAuditAdapter = {
  record(input: { actorId: string; action: string; entityType: string; entityId: string; reason: string }) {
    const s = load();
    s.audit.unshift({ id: crypto.randomUUID(), actorId: input.actorId, action: input.action, entityType: input.entityType, entityId: input.entityId, reason: input.reason, occurredAt: new Date().toISOString() });
    save(s);
  },
};
